import { useRef } from 'react'
import { ChevronRight } from 'lucide-react'
import type { League, LeagueMember } from '../types'
import PageHeader from './PageHeader'
import './LeaguesList.css'

interface LeaguesListProps {
  leagues: League[]
  // Commit handler for a tapped row. The second arg is the row's league-name node — the source for
  // the shared-element flight. Callers that don't animate (e.g. the profile's
  // leagues-at-a-glance) simply ignore it.
  onSelectLeague: (leagueId: string, sourceNameEl?: HTMLElement | null) => void
  onCreateLeague: () => void
  onJoinLeague: () => void
  // Opens the Explore directory. Optional so the non-app hosts of this list (the
  // profile's leagues-at-a-glance, the rules demo) don't have to supply a route they don't have.
  onExploreLeagues?: () => void
  // Touch-down feedback: fire the light-impact haptic immediately on press, before
  // any commit. Optional so non-animating hosts can omit it.
  onPressFeedback?: () => void
  // Blocks row taps while a league enter/exit transition is running.
  transitionActive?: boolean
  // When set, the season hasn't opened yet: every row swaps its rank column for an en dash and
  // its meta line for `Season starts {label}`. null/undefined = season underway.
  seasonStartLabel?: string | null
}

const EN_DASH = '–'

// System (mandatory, auto-joined) leagues belong under "Open leagues"; everything else is a
// league the user joined or created ("Your leagues"). The `isSystemLeague` flag is the intended
// source of truth, but it's a mutable column that was backfilled late on production — a global/club
// row that predates it can carry `false` and wrongly land in "Your leagues". So we fall back to the
// immutable `type`: global and club are always system leagues.
function isSystemLeague(league: League): boolean {
  return league.isSystemLeague || league.type === 'global' || league.type === 'club'
}

function leaderOf(league: League): LeagueMember | undefined {
  // Standings arrive rank-sorted from the server, so the leader is simply the first row.
  return league.standings[0]
}

function metaLineFor(league: League, seasonStartLabel?: string | null): string {
  if (seasonStartLabel) return `Season starts ${seasonStartLabel}`
  const players = `${league.memberCount.toLocaleString()} players`
  const leader = leaderOf(league)
  if (!leader) return players
  return leader.isYou ? `${players} · you lead` : `${players} · ${leader.name} leads`
}

// The rank rail: delta then rank as a single horizontal unit on one baseline. The delta is the
// isYou row's movement since the last kickoff snapshot; no movement (null/0) renders as a muted
// en dash.
function RankColumn({
  league,
  seasonStartLabel,
}: {
  league: League
  seasonStartLabel?: string | null
}) {
  if (seasonStartLabel) {
    return (
      <div className="league-row__rank" aria-label="Rank not available yet">
        <span className="league-row__rank-num league-row__rank-num--empty">{EN_DASH}</span>
      </div>
    )
  }

  const you = league.standings.find((m) => m.isYou)
  const delta = you?.movementDelta ?? null

  let deltaNode
  if (delta && delta > 0) {
    deltaNode = (
      <span className="league-row__delta league-row__delta--up">{`↑${delta}`}</span>
    )
  } else if (delta && delta < 0) {
    deltaNode = (
      <span className="league-row__delta league-row__delta--down">{`↓${Math.abs(delta)}`}</span>
    )
  } else {
    deltaNode = (
      <span className="league-row__delta league-row__delta--none" aria-hidden="true">
        {EN_DASH}
      </span>
    )
  }

  return (
    <div className="league-row__rank">
      {deltaNode}
      <span className="league-row__rank-num">{league.yourRank.toLocaleString()}</span>
    </div>
  )
}

/** Exported for the rules screen's leagues demo, which renders two frozen rows so
 *  the rules teach the real row rather than a drawing of it. */
export function LeagueRow({
  league,
  onSelect,
  onPressFeedback,
  seasonStartLabel,
}: {
  league: League
  // Receives the row's league-name node so the caller can measure the shared-element source rect.
  onSelect: (sourceNameEl: HTMLElement | null) => void
  onPressFeedback?: () => void
  seasonStartLabel?: string | null
}) {
  const nameRef = useRef<HTMLParagraphElement>(null)
  const commit = () => onSelect(nameRef.current)

  return (
    <div
      className="league-row"
      role="button"
      tabIndex={0}
      onPointerDown={onPressFeedback}
      onClick={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          commit()
        }
      }}
    >
      <div className="league-row__identity">
        <p className="league-row__name" ref={nameRef}>
          {league.name}
        </p>
        <p className="league-row__meta">{metaLineFor(league, seasonStartLabel)}</p>
      </div>
      <RankColumn league={league} seasonStartLabel={seasonStartLabel} />
    </div>
  )
}

function LeaguesList({
  leagues,
  onSelectLeague,
  onCreateLeague,
  onJoinLeague,
  onExploreLeagues,
  onPressFeedback,
  transitionActive,
  seasonStartLabel,
}: LeaguesListProps) {
  // Categorical split (not preference-based): leagues the user joined or created vs. the leagues
  // they're in automatically (global, favourite-team). Section headers only render when BOTH
  // sections are non-empty.
  const yourLeagues = leagues.filter((l) => !isSystemLeague(l)).sort((a, b) => a.yourRank - b.yourRank)
  const openLeagues = leagues
    .filter(isSystemLeague)
    .sort((a, b) => (a.type === 'global' ? -1 : b.type === 'global' ? 1 : 0))
  const showHeaders = yourLeagues.length > 0 && openLeagues.length > 0

  // Join / Create pills live in the shared page header, unchanged —
  // invites are a per-league action and belong on the detail screen, not here.
  const headerActions = (
    <>
      <button
        className="leagues-list__action leagues-list__action--secondary"
        onClick={onJoinLeague}
      >
        Join league
      </button>
      <button
        className="leagues-list__action leagues-list__action--primary"
        onClick={onCreateLeague}
      >
        + Create league
      </button>
    </>
  )

  const renderRow = (league: League) => (
    <LeagueRow
      key={league.id}
      league={league}
      seasonStartLabel={seasonStartLabel}
      onPressFeedback={onPressFeedback}
      onSelect={(sourceNameEl) => onSelectLeague(league.id, sourceNameEl)}
    />
  )

  return (
    <div
      className="leagues-list"
      // Landing taps are blocked while a league enter/exit transition runs.
      style={transitionActive ? { pointerEvents: 'none' } : undefined}
    >
      <PageHeader title="Leagues" actions={headerActions} />

      <div className="leagues-list__sections">
        {yourLeagues.length > 0 && (
          <section className="leagues-list__section">
            {showHeaders && <p className="leagues-list__section-label label-eyebrow">Your leagues</p>}
            <div className="leagues-list__rows">{yourLeagues.map(renderRow)}</div>
          </section>
        )}

        {openLeagues.length > 0 && (
          <section className="leagues-list__section leagues-list__section--open">
            {showHeaders && <p className="leagues-list__section-label label-eyebrow">Open leagues</p>}
            <div className="leagues-list__rows">{openLeagues.map(renderRow)}</div>
          </section>
        )}

        {/* The way in for someone with no invite code. Under the sections rather than
            beside the header pills: those are the two things you do when you already know which
            league you want, and this is the one you press when you don't.

            Styled as the profile footer's navigation row, not as a league: a boxed row directly
            under boxed league rows read as a fourth league. It keeps a chevron, which that footer
            does not, because this is one row pushing to one screen rather than two labels. */}
        {onExploreLeagues && (
          <button type="button" className="leagues-list__explore" onClick={onExploreLeagues}>
            <span className="leagues-list__explore-text">
              <span className="leagues-list__explore-title">Explore leagues</span>
              <span className="leagues-list__explore-sub">Find a league to join</span>
            </span>
            <ChevronRight className="leagues-list__explore-chevron" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  )
}

export default LeaguesList
